import { FC } from 'react';

import { CouponSendPendingRow } from '@shared/api/couponSettlement/couponSettlement.interface';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { couponCodeLabel, formatFee, won } from '../../utils/coupon-settlement.utils';
import { formatDate } from '../../utils/delivery.utils';

export interface CouponSendPendingTableProps {
  /** 표시 행 (검색·페이지 슬라이스 적용분) */
  rows: CouponSendPendingRow[];
  /** 빈 상태 문구 — 전체가 없는지/검색 결과만 없는지는 호출부가 판단 */
  emptyMessage: string;
  /** 순번 시작 오프셋 ((page−1)×PAGE_SIZE) */
  startIndex: number;
  /** view 전용이면 체크박스 열 제거 */
  readOnly: boolean;
  /** 제외 선택(쿠폰 id 단위) — 체크됨 = 포함. 초과(overQuota) 행은 선택 불가 */
  excl: Set<string>;
  onToggle: (couponId: string) => void;
  /** 헤더 전체 체크 상태/토글 — 검색 결과의 선택 가능 행 기준 (호출부 계산) */
  allChecked: boolean;
  onToggleAll: () => void;
}

/**
 * 미정산 발송 수익금 테이블 — 초과(overQuota) 행은 앰버 표시 + 체크 비활성.
 * 초과건 = 대응 구매 없음 → 구매금액 0원(수수료 0원), 정산금 칸에는 −도매가
 * (발송 실비를 상조에서 회수할 마이너스 건 — 낙전 정산에서 처리)를 보여준다.
 */
export const CouponSendPendingTable: FC<CouponSendPendingTableProps> = ({
  rows,
  emptyMessage,
  startIndex,
  readOnly,
  excl,
  onToggle,
  allChecked,
  onToggleAll,
}) => {
  // 빈 상태 colSpan — readOnly면 체크박스 열이 없다
  const colSpan = readOnly ? 13 : 14;
  return (
    <div className="overflow-x-auto rounded-lg bg-white shadow">
      <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
        <colgroup>
          {!readOnly && <col className="w-[3%]" />}
          <col className="w-[3%]" />
          <col className="w-[7%]" />
          <col className="w-[12%]" />
          <col className="w-[10%]" />
          <col className="w-[8%]" />
          <col className="w-[9%]" />
          <col className="w-[6%]" />
          <col className="w-[10%]" />
          <col className="w-[6%]" />
          <col className="w-[5%]" />
          <col className="w-[7%]" />
          <col className="w-[7%]" />
          <col className="w-[7%]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-500">
          <tr>
            {!readOnly && (
              <th className="w-10 px-3 py-2 text-center">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={onToggleAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </th>
            )}
            <th className="px-3 py-2 text-center">순번</th>
            <th className="px-3 py-2 text-left">쿠폰번호</th>
            <th className="px-3 py-2 text-left">구매상품명</th>
            <th className="px-3 py-2 text-left">
              <p>구매자</p>
              <p className="mt-0.5">연락처</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>고인명</p>
              <p className="mt-0.5">발인일</p>
            </th>
            <th className="px-3 py-2 text-left">
              <p>구매일</p>
              <p className="mt-0.5">발송일</p>
            </th>
            <th className="px-3 py-2 text-right">판매가</th>
            <th className="px-3 py-2 text-left">
              <p>수령상품</p>
              <p className="mt-0.5">정상가</p>
            </th>
            <th className="px-3 py-2 text-right">도매가</th>
            <th className="px-3 py-2 text-right">수수료</th>
            <th className="px-3 py-2 text-right">정산금(+)</th>
            <th className="px-3 py-2 text-left">
              <p>소속</p>
              <p className="mt-0.5">지도사명</p>
            </th>
            <th className="px-3 py-2 text-left">지도사연락처</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="px-3 py-6 text-center text-gray-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((r, idx) => {
              const included = !r.overQuota && !excl.has(r.giftCouponId);
              return (
                <tr
                  key={r.giftCouponId}
                  onClick={() => !readOnly && !r.overQuota && onToggle(r.giftCouponId)}
                  title={
                    r.overQuota
                      ? '구매수량 초과 발송 — 대응 구매가 없어 구매금액 0원(수수료 0원)이고, 도매가(발송 실비)는 낙전 정산에서 상조 상계 예정입니다.'
                      : undefined
                  }
                  className={
                    r.overQuota
                      ? 'bg-amber-50/60'
                      : readOnly
                      ? 'hover:bg-gray-50'
                      : `cursor-pointer ${
                          included
                            ? 'bg-emerald-50/40 hover:bg-emerald-50'
                            : 'opacity-50 hover:bg-gray-50'
                        }`
                  }
                >
                  {!readOnly && (
                    <td className="px-3 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={included}
                        readOnly
                        disabled={r.overQuota}
                        className="h-4 w-4 rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-40"
                      />
                    </td>
                  )}
                  <td className="px-3 py-3 text-center text-xs tabular-nums text-gray-500">
                    {startIndex + idx + 1}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-700">
                    <p className="whitespace-nowrap">{couponCodeLabel(r.fullCode)}</p>
                    {r.overQuota && (
                      <span className="mr-1 mt-0.5 inline-block whitespace-nowrap rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-700">
                        구매수량 초과
                      </span>
                    )}
                    {/* 재발행 뱃지 — 지급되는 정상 행 표시 (상조 view에도 동일 노출, 2026-07-02 결정) */}
                    {r.isAdditional && (
                      <span className="mt-0.5 inline-block whitespace-nowrap rounded bg-sky-100 px-1 py-0.5 text-[10px] font-medium text-sky-700">
                        재발행
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <p className="break-words text-xs font-medium text-gray-700">
                      {r.purchaseItemName}
                    </p>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <p className="font-bold text-gray-700">{r.buyerName || '-'}</p>
                    {r.buyerPhoneNumber && (
                      <p className="mt-0.5 text-gray-400">
                        {phoneNumberStringFormator(r.buyerPhoneNumber)}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    <p>{r.deceasedName || '-'}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {r.coffinOutDate ? formatDate(r.coffinOutDate) : '-'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    <p>{formatDate(r.purchasedAt)}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {formatDate(r.deliveredAt)}
                    </p>
                  </td>
                  {/* 초과건 = 대응 구매 없음 → 구매금액 0원 (수수료도 0원) */}
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-700">
                    {r.overQuota ? '0' : won(r.priceRetail)}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600">
                    {/* 수령상품명은 길어서 말줄임 — 전체 이름은 hover 툴팁으로 */}
                    <p className="truncate" title={r.receiveItemName || undefined}>
                      {r.receiveItemName || '-'}
                    </p>
                    <p className="mt-0.5 tabular-nums text-gray-500">
                      {won(r.priceRegular)}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                    {won(r.priceWhole)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                    {r.overQuota ? '0' : r.fee != null ? formatFee(r.fee) : '-'}
                  </td>
                  {/* 초과건 정산금 = −도매가 (지급 보류가 아니라 발송 실비를
                      상조에서 회수할 마이너스 건 — 낙전 정산에서 처리) */}
                  <td
                    className={`whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums ${
                      r.overQuota ? 'text-rose-700' : 'text-emerald-700'
                    }`}
                  >
                    {r.overQuota
                      ? r.priceWhole != null
                        ? `−${r.priceWhole.toLocaleString('ko-KR')}`
                        : '-'
                      : `+${r.amount.toLocaleString('ko-KR')}`}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    <p>{r.teamName || '-'}</p>
                    <p className="mt-0.5 font-bold text-gray-700">
                      {r.workerName || '-'}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {r.workerPhoneNumber
                      ? phoneNumberStringFormator(r.workerPhoneNumber)
                      : '-'}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
